export type MbbeRuntimeConfig = {
  API_BASE_URL?: string;
  FIREBASE_API_KEY?: string;
  FIREBASE_AUTH_DOMAIN?: string;
  FIREBASE_PROJECT_ID?: string;
  FIREBASE_STORAGE_BUCKET?: string;
  FIREBASE_MESSAGING_SENDER_ID?: string;
  FIREBASE_WEB_PUSH_VAPID_KEY?: string;
  FIREBASE_APP_ID?: string;
  TURNSTILE_SITE_KEY?: string;
  GTM_ID?: string;
};

type RuntimeGlobal = typeof globalThis & {
  __MBBE_CONFIG__?: MbbeRuntimeConfig;
};

export function getRuntimeConfig(): MbbeRuntimeConfig {
  return (globalThis as RuntimeGlobal).__MBBE_CONFIG__ || {};
}

export function getRuntimeConfigValue(
  key: keyof MbbeRuntimeConfig,
  fallback?: string
): string {
  return getRuntimeConfig()[key] || fallback || '';
}

export type DeployedRuntimeIdentity = {
  firebaseProjectId: string;
  apiBaseUrl: string;
  apiOrigin: string;
};

function requiredString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${label} is required for deployed runtime evidence`);
  }
  return value.trim();
}

/**
 * Validate only the public runtime identity used by deployed browser evidence.
 * This intentionally does not return API keys, tokens, or other credentials.
 */
export function assertDeployedRuntimeIdentity(
  config: MbbeRuntimeConfig,
  expected: { firebaseProjectId: string; apiBaseUrl: string },
): DeployedRuntimeIdentity {
  const firebaseProjectId = requiredString(
    config.FIREBASE_PROJECT_ID,
    'FIREBASE_PROJECT_ID',
  );
  const apiBaseUrl = requiredString(config.API_BASE_URL, 'API_BASE_URL');
  const expectedProjectId = requiredString(
    expected.firebaseProjectId,
    'expected Firebase project ID',
  );
  const expectedApiBaseUrl = requiredString(
    expected.apiBaseUrl,
    'expected API base URL',
  );

  let actualUrl: URL;
  let expectedUrl: URL;
  try {
    actualUrl = new URL(apiBaseUrl);
    expectedUrl = new URL(expectedApiBaseUrl);
  } catch {
    throw new Error('deployed API base URL must be an absolute HTTPS URL');
  }
  if (
    actualUrl.protocol !== 'https:' ||
    expectedUrl.protocol !== 'https:' ||
    actualUrl.username ||
    actualUrl.password ||
    actualUrl.search ||
    actualUrl.hash ||
    expectedUrl.username ||
    expectedUrl.password ||
    expectedUrl.search ||
    expectedUrl.hash
  ) {
    throw new Error('deployed API base URL must be an HTTPS URL without credentials');
  }
  if (firebaseProjectId !== expectedProjectId) {
    throw new Error(
      `deployed Firebase project mismatch: expected ${expectedProjectId}, received ${firebaseProjectId}`,
    );
  }
  if (actualUrl.href.replace(/\/$/, '') !== expectedUrl.href.replace(/\/$/, '')) {
    throw new Error(
      `deployed API base URL mismatch: expected ${expectedUrl.href}, received ${actualUrl.href}`,
    );
  }

  return {
    firebaseProjectId,
    apiBaseUrl: actualUrl.href.replace(/\/$/, ''),
    apiOrigin: actualUrl.origin,
  };
}

export function deployedHealthUrl(apiBaseUrl: string): string {
  const url = new URL(requiredString(apiBaseUrl, 'API_BASE_URL'));
  if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) {
    throw new Error('deployed health endpoint requires an HTTPS API URL without credentials');
  }
  const basePath = url.pathname.replace(/\/$/, '');
  url.pathname = `${basePath}/health`;
  url.search = '';
  url.hash = '';
  return url.href;
}
